import { closestCenter, DndContext, DragEndEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { ViewColumn } from '@mui/icons-material';
import { Box, Button, Divider, FormGroup, Menu, PopoverVirtualElement } from '@mui/material';
import { Column, Table } from '@tanstack/react-table';
import React from 'react';

import { useTheme } from '@/theme';

import StyledToolbarMenuItem from './StyledToolbarMenuItem';

type Forbid<T extends object> = {
  [K in keyof T]?: never;
};

type ColumnManagementProps = {
  isMenuOpen: boolean;
  onOpenMenu: React.MouseEventHandler<HTMLButtonElement> | undefined;
  onCloseMenu: ((event: object, reason: 'backdropClick' | 'escapeKeyDown') => void) | undefined;
  menuAnchorEl: Element | PopoverVirtualElement | (() => Element | PopoverVirtualElement | null) | null | undefined;
};

type ExportFeatureProps = {
  onExport?: React.MouseEventHandler<HTMLButtonElement> | undefined;
  isExportLoading?: boolean;
  isExportDisabled?: boolean;
};

type TableVisibilityFeatureProps<TData> = {
  /**
   * Specify always visible columns in StyledTable component
   */
  alwaysVisibleColumns?: Column<TData, unknown>[];
};

type TableColumnDndFeatureProps = {
  /**
   * Resets column order to default
   */
  onColumnsOrderReset?: () => void;
  /**
   * Specifies when reset button is disabled
   */
  isResetDisabled?: boolean;
};

type BaseTableProps<TData> = {
  /**
   * Table component returned from useTablePagination
   */
  tableComponent?: Table<TData>;
  /**
   * Export button functions, enables Export button visibility in UI
   */
  exportFeature?: ExportFeatureProps;
};

type EnabledColumnsVisibilityProps<TData> = BaseTableProps<TData> & {
  /**
   * Enables columns visibility feature
   *
   * When `true` - component becomes interactive and allows to toggle columns visibility,
   * requires all `ColumnManagementProps` and `TableVisibilityFeatureProps`
   */
  isColumnVisibilityEnabled: true;
  /**
   * Disables columns order feature
   *
   * When `false` - column order feature does not become interactive and does not allow to change columns order
   * `TableColumnDndFeatureProps` are not required
   */
  isDndEnabled: false;
} & ColumnManagementProps &
  TableVisibilityFeatureProps<TData> &
  Forbid<TableColumnDndFeatureProps>;

type EnabledColumnOrderProps<TData> = BaseTableProps<TData> & {
  /**
   * Disables columns visibility feature
   *
   * When `false` - component does not become interactive and does now allow toggling columns visibility,
   * `TableVisibilityFeatureProps` are not required
   */
  isColumnVisibilityEnabled: false;
  /**
   * Enables columns order feature
   *
   * When `true` - component becomes interactive and allows to change columns order,
   * requires all `ColumnManagementProps` and `TableColumnDndFeatureProps`
   */
  isDndEnabled: true;
} & ColumnManagementProps &
  TableColumnDndFeatureProps &
  Forbid<TableVisibilityFeatureProps<TData>>;

type EnabledCompleteColumnManagementProps<TData> = BaseTableProps<TData> & {
  /**
   * Enables columns visibility feature
   *
   * When `true` - component becomes interactive and allows to toggle columns visibility,
   * requires all `ColumnManagementProps` and `TableVisibilityFeatureProps`
   */
  isColumnVisibilityEnabled: true;
  /**
   * Enables columns order feature
   *
   * When `true` - component becomes interactive and allows to change columns order,
   * requires all `ColumnManagementProps` and `TableColumnDndFeatureProps`
   */
  isDndEnabled: true;
} & ColumnManagementProps &
  TableColumnDndFeatureProps &
  TableVisibilityFeatureProps<TData>;

type DisabledCompleteColumnManagementProps<TData> = Partial<BaseTableProps<TData>> & {
  /**
   * Disables columns visibility feature
   *
   * When `false` - component does not become interactive and does now allow toggling columns visibility,
   * `ColumnManagementProps` and `TableVisibilityFeatureProps` are not required
   */
  isColumnVisibilityEnabled: false;
  /**
   * Disables columns order feature
   *
   * When `false` - column order feature does not become interactive and does not allow to change columns order
   * `ColumnManagementProps` and `TableColumnDndFeatureProps` are not required
   */
  isDndEnabled: false;
} & Forbid<ColumnManagementProps> &
  Forbid<TableVisibilityFeatureProps<TData>> &
  Forbid<TableColumnDndFeatureProps>;

type StyledTableToolbarProps<TData> =
  | EnabledColumnsVisibilityProps<TData>
  | DisabledCompleteColumnManagementProps<TData>
  | EnabledColumnOrderProps<TData>
  | EnabledCompleteColumnManagementProps<TData>;

function StyledTableToolbar<TData>(props: StyledTableToolbarProps<TData>) {
  const theme = useTheme();

  const allColumns =
    (props.tableComponent?.getState().columnOrder.length ?? 0) > 0
      ? (props.tableComponent?.getState().columnOrder ?? [])
      : (props.tableComponent?.getAllLeafColumns().map((col) => col.id) ?? []);

  const lockedColumns = new Set<string>(props.alwaysVisibleColumns?.map((col) => col.id));

  const visibleColumns = allColumns.filter((columnId) => !lockedColumns.has(columnId));

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = visibleColumns.indexOf(String(active.id));
    const newIndex = visibleColumns.indexOf(String(over.id));

    if (oldIndex === -1 || newIndex === -1) return;

    const nextVisible = arrayMove(visibleColumns, oldIndex, newIndex);

    let visibleIndex = 0;

    const nextAll = allColumns.map((columnId) => (lockedColumns.has(columnId) ? columnId : nextVisible[visibleIndex++]));

    props.tableComponent?.setColumnOrder(nextAll);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
      {(props.isColumnVisibilityEnabled || props.isDndEnabled) && (
        <Button
          aria-label='manage-columns'
          data-testid='manage-columns-button'
          variant='outlined'
          sx={{
            borderColor: theme.hhglobal.purpose.textStrong,
            color: theme.hhglobal.purpose.textStrong,
            height: '36px',
            textTransform: 'none',
            alignSelf: 'flex-end',
            marginLeft: 'auto',
          }}
          onClick={props.onOpenMenu}
        >
          <ViewColumn sx={{ fontSize: '24px', color: '#000' }} />
        </Button>
      )}
      <Menu
        id='manage-columns-menu'
        anchorEl={props.menuAnchorEl}
        open={props.isMenuOpen ? true : false}
        onClose={props.onCloseMenu}
        disableScrollLock
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              p: 0,
              '& .MuiMenu-list': {
                p: 0,
              },
            },
          },
          list: {
            'aria-labelledby': 'column-visibility-button',
          },
        }}
      >
        {props.isDndEnabled && props.onColumnsOrderReset && (
          <Box sx={{ width: '100%' }}>
            <Button
              aria-label='column-order-reset'
              data-testid='column-order-reset-button'
              variant='text'
              onClick={props.onColumnsOrderReset}
              sx={{
                fontWeight: theme.hhglobal.typography.fontWeights.bold,
                color: theme.hhglobal.purpose.textStrong,
                textTransform: 'none',
              }}
              disabled={props.isResetDisabled}
            >
              Reset order
            </Button>
            <Divider />
          </Box>
        )}
        <FormGroup sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          {props.isDndEnabled ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={onDragEnd}
              modifiers={[restrictToParentElement, restrictToVerticalAxis]}
            >
              <SortableContext items={visibleColumns} strategy={verticalListSortingStrategy}>
                {visibleColumns.map((columnId) => {
                  const column = props.tableComponent?.getColumn(columnId);

                  if (!column) return null;

                  return <StyledToolbarMenuItem key={columnId} column={column} isDndEnabled={props.isDndEnabled} />;
                })}
              </SortableContext>
            </DndContext>
          ) : (
            props.tableComponent
              ?.getAllLeafColumns()
              .filter((column) => props.alwaysVisibleColumns && !props.alwaysVisibleColumns?.includes(column))
              .map((column) => <StyledToolbarMenuItem key={column.id} column={column} isDndEnabled={false} />)
          )}
        </FormGroup>
      </Menu>

      {props.exportFeature && props.exportFeature.onExport && (
        <Button
          variant='contained'
          data-testid='export-button'
          loading={props.exportFeature.isExportLoading}
          sx={{
            borderColor: theme.hhglobal.purpose.textStrong,
            color: theme.hhglobal.purpose.fillWeak,
            height: '36px',
            textTransform: 'none',
            alignSelf: 'flex-end',
            marginLeft: 'auto',
          }}
          onClick={props.exportFeature.onExport}
          disabled={props.exportFeature.isExportDisabled}
        >
          Export
        </Button>
      )}
    </Box>
  );
}

export default StyledTableToolbar;
